import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { formatCZK, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { StatCard } from "@/components/shared/PagePrimitives";
import { SupplierEditDialog } from "@/components/suppliers/SupplierEditDialog";
import { StatusPill } from "@/components/orders/OrderBits";
import { dueText, projectLabel, todayISO } from "@/lib/orders";
import { ArrowLeft, Truck, User, Mail, Phone, FileText, MapPin, StickyNote, Pencil, Trash2, ExternalLink, Package, Store, Globe, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { ask } from "@/lib/confirm";

function ContactRow({ icon: Icon, label, value, href, ext }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5 text-sm">
      <Icon size={15} className="text-muted-foreground shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        {href ? <a href={href} target={ext ? "_blank" : undefined} rel={ext ? "noopener noreferrer" : undefined}
          className="text-brand hover:underline break-words inline-flex items-center gap-1">{value}{ext && <ExternalLink size={12} />}</a>
          : <span className="font-semibold break-words">{value}</span>}
      </div>
    </div>
  );
}

const webHref = (w) => (/^https?:\/\//i.test(w) ? w : `https://${w}`);

// 314: detail dodavatele — jako investor: údaje, kontakt, pobočky a jeho objednávky.
export default function SupplierDetail() {
  const { sid } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [orders, setOrders] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/suppliers/${sid}`).then((r) => setS(r.data)).catch(() => { toast.error("Dodavatel nenalezen."); nav("/dodavatele"); });
    api.get("/orders", { params: { status: "all", supplier_id: sid } }).then((r) => setOrders(r.data.items || [])).catch(() => setOrders([]));
  }, [sid, nav]);
  useEffect(() => { load(); }, [load]);

  if (!s) return <div className="text-muted-foreground">Načítám…</div>;
  const today = todayISO();
  const address = [s.street, [s.zip, s.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const list = [...(orders || [])].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const sum = list.filter((o) => o.status !== "cancelled").reduce((a, o) => a + (o.amount_gross || 0), 0);
  const money = list.some((o) => "amount_gross" in o);

  const remove = async () => {
    if (!await ask(`Smazat dodavatele „${s.name}"?`)) return;
    try { await api.delete(`/suppliers/${sid}`); toast.success("Dodavatel smazán."); nav("/dodavatele"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="supplier-detail-page">
      <Link to="/dodavatele" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> Zpět na dodavatele</Link>

      <div className="bg-card border border-border rounded-[20px] p-5 md:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-brand/10 text-brand flex items-center justify-center shrink-0"><Truck size={26} /></div>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-black tracking-tight truncate" data-testid="supplier-title">{s.name}</h1>
          <div className="text-sm text-muted-foreground mt-0.5 truncate">{[s.contact_person, s.city, s.ico && `IČO ${s.ico}`].filter(Boolean).join(" · ") || "Dodavatel"}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="supplier-edit-btn" className="h-10 flex-1 sm:flex-none"><Pencil size={15} /> Upravit</Button>
          {!s.orders_total && <Button variant="ghost" onClick={remove} data-testid="supplier-delete-btn" aria-label="Smazat dodavatele" className="h-10 text-destructive hover:text-destructive"><Trash2 size={15} /></Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Package} label="Objednávky" value={s.orders_total || 0} testid="supplier-stat-total" />
        <StatCard icon={Package} label="Rozpracované" value={s.orders_open || 0} brand testid="supplier-stat-open" />
        {money && <StatCard icon={FileText} label="Objednáno celkem" value={formatCZK(sum)} sub="s DPH, bez zrušených" testid="supplier-stat-sum" />}
        <StatCard icon={CalendarDays} label="Naposledy" value={s.last_order_at ? formatDate(s.last_order_at) : "—"} testid="supplier-stat-last" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="flex flex-col gap-5 min-w-0">
          <div className="bg-card border border-border rounded-[20px] p-5 space-y-3">
            <h2 className="font-bold flex items-center gap-2"><User size={16} className="text-brand" /> Kontakt & údaje</h2>
            <ContactRow icon={User} label="Kontaktní osoba" value={s.contact_person} />
            <ContactRow icon={Mail} label="E-mail" value={s.email} href={s.email ? `mailto:${s.email}` : null} />
            <ContactRow icon={Phone} label="Telefon" value={s.phone} href={s.phone ? `tel:${s.phone.replace(/\s/g, "")}` : null} />
            <ContactRow icon={Globe} label="Web / e-shop" value={s.web} href={s.web ? webHref(s.web) : null} ext />
            <ContactRow icon={MapPin} label="Sídlo" value={address} />
            <ContactRow icon={FileText} label="IČO" value={s.ico} href={s.ico ? `https://ares.gov.cz/ekonomicke-subjekty?ico=${s.ico}` : null} ext />
            <ContactRow icon={FileText} label="DIČ" value={s.dic} />
            {!s.contact_person && !s.email && !s.phone && !s.ico && !address && (
              <p className="text-sm text-muted-foreground">Žádné údaje. Doplňte IČO tlačítkem Upravit — zbytek se načte z ARES.</p>
            )}
          </div>
          {(s.branches || []).length > 0 && (
            <div className="bg-card border border-border rounded-[20px] p-5 space-y-2.5" data-testid="supplier-branches-card">
              <h2 className="font-bold flex items-center gap-2"><Store size={16} className="text-brand" /> Pobočky na vyzvednutí</h2>
              <div className="flex flex-wrap gap-1.5">
                {s.branches.map((b) => <span key={b.id} className="h-8 px-3 rounded-full bg-secondary text-[13px] font-semibold inline-flex items-center">{b.name}</span>)}
              </div>
            </div>
          )}
          {s.note && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-[20px] p-5 space-y-2.5">
              <h2 className="font-bold flex items-center gap-2 text-amber-700 dark:text-amber-400"><StickyNote size={16} /> Poznámka</h2>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{s.note}</p>
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-[20px] p-5 md:col-span-2 min-w-0">
          <h2 className="font-bold flex items-center gap-2 mb-3"><Package size={16} className="text-brand" /> Objednávky u dodavatele ({list.length})</h2>
          {orders && !list.length && <p className="text-sm text-muted-foreground">Zatím žádná objednávka. Dodavatele vyberete v Objednávkách → Nová objednávka.</p>}
          <div className="space-y-2" data-testid="supplier-orders">
            {list.map((o) => (
              <button key={o.id} type="button" onClick={() => nav(`/objednavky?o=${o.id}`)} data-testid={`supplier-order-${o.id}`}
                className="w-full flex items-center gap-3 p-3 rounded-[14px] border border-border text-left hover:border-brand transition-colors motion-reduce:transition-none">
                <div className="min-w-0 flex-1">
                  <div className="font-bold truncate">{o.title}</div>
                  <div className="text-xs text-muted-foreground truncate">{[o.order_no && `č. ${o.order_no}`, projectLabel(o), dueText(o, today)].filter(Boolean).join(" · ")}</div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <StatusPill o={o} />
                  {o.amount_gross != null && <span className="text-xs font-semibold tabular-nums">{formatCZK(o.amount_gross)}</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <SupplierEditDialog open={editOpen} onOpenChange={setEditOpen} supplier={s} onSaved={load} />
    </div>
  );
}
